import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { Loader } from '@/components/shared';
import { Functions, Databases, Query } from 'appwrite';
import { appwriteConfig, client } from '@/lib/appwrite/config';
import BusinessSLA from '@/_auth/forms/BusinessSLA';
import { logTrustScoreEvent } from '@/lib/appwrite/trustScore';

type VerificationStep = 'pan' | 'aadhaar_otp' | 'aadhaar_verify' | 'bank';
type VerificationStatus = {
  pan: boolean;
  aadhaar: boolean;
  bank: boolean;
};

export function KYCVerificationModal({
  userId,
  onComplete,
  onClose
}: {
  userId: string;
  onComplete: (status: VerificationStatus) => void;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [step, setStep] = useState<VerificationStep>('pan');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<VerificationStatus>({
    pan: false,
    aadhaar: false,
    bank: false
  });
  
  // Form data
  const [panData, setPanData] = useState({ pan: '', name: '' });
  const [aadhaarData, setAadhaarData] = useState({ aadhaarNumber: '', otp: '', refId: '' });
  const [bankData, setBankData] = useState({ accountNumber: '', ifsc: '', name: '', phone: '' });

  // Check existing KYC status on load
  useEffect(() => {
    const checkExistingKYC = async () => {
      try {
        const databases = new Databases(client);
        const response = await databases.listDocuments(
          appwriteConfig.databaseId,
          appwriteConfig.kycRecordsCollectionId,
          [Query.equal('userId', userId)]
        );

        if (response.documents.length > 0) {
          const kycData = response.documents[0];
          const newStatus = {
            pan: kycData.panVerified || false,
            aadhaar: kycData.aadhaarVerified || false,
            bank: kycData.bankVerified || false
          };
          setStatus(newStatus);

          // Set initial step based on what's already completed
          if (!newStatus.pan) setStep('pan');
          else if (!newStatus.aadhaar) setStep('aadhaar_otp');
          else if (!newStatus.bank) setStep('bank');
        }
      } catch (error) {
        console.error('Error checking existing KYC:', error);
      }
    };

    checkExistingKYC();
  }, [userId]);

  const verifyPan = async () => {
    setLoading(true);
    try {
      const functions = new Functions(client);
      const response = await functions.createExecution(
        appwriteConfig.verifyKYCFunctionId,
        JSON.stringify({
          action: 'verify_pan',
          userId,
          pan: panData.pan,
          name: panData.name
        })
      );

      const result = JSON.parse(response.responseBody);
      if (result.success) {
        const newStatus = { ...status, pan: true };
        setStatus(newStatus);
        toast({ title: 'PAN Verified Successfully' });
        
        // Continue to next incomplete step or complete
        if (!newStatus.aadhaar) {
          setStep('aadhaar_otp');
        } else if (!newStatus.bank) {
          setStep('bank');
        } else {
          onComplete(newStatus);
        }
      } else {
        toast({
          title: 'PAN Verification Failed',
          description: result.message || 'Please check your details',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to verify PAN',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const generateAadhaarOTP = async () => {
    setLoading(true);
    try {
      const functions = new Functions(client);
      const response = await functions.createExecution(
        appwriteConfig.verifyKYCFunctionId,
        JSON.stringify({
          action: 'generate_aadhaar_otp',
          userId,
          aadhaarNumber: aadhaarData.aadhaarNumber
        })
      );

      const result = JSON.parse(response.responseBody);
      if (result.success) {
        setAadhaarData(prev => ({ ...prev, refId: result.ref_id }));
        toast({ title: 'OTP Sent Successfully' });
        setStep('aadhaar_verify');
      } else {
        toast({
          title: 'Failed to Send OTP',
          description: result.message || 'Please try again',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to generate OTP',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyAadhaarOTP = async () => {
    setLoading(true);
    try {
      const functions = new Functions(client);
      const response = await functions.createExecution(
        appwriteConfig.verifyKYCFunctionId,
        JSON.stringify({
          action: 'verify_aadhaar_otp',
          userId,
          otp: aadhaarData.otp,
          refId: aadhaarData.refId
        })
      );

      const result = JSON.parse(response.responseBody);
      if (result.success) {
        const newStatus = { ...status, aadhaar: true };
        setStatus(newStatus);
        toast({ title: 'Aadhaar Verified Successfully' });
        
        // Continue to next incomplete step or complete
        if (!newStatus.bank) {
          setStep('bank');
        } else {
          onComplete(newStatus);
        }
      } else {
        toast({
          title: 'Aadhaar Verification Failed',
          description: result.message || 'Please check your OTP',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to verify Aadhaar',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyBankDetails = async () => {
    setLoading(true);
    try {
      const functions = new Functions(client);
      const response = await functions.createExecution(
        appwriteConfig.verifyKYCFunctionId,
        JSON.stringify({
          action: 'verify_bank',
          userId,
          ...bankData
        })
      );

      const result = JSON.parse(response.responseBody);
      if (result.success) {
        const newStatus = { ...status, bank: true };
        setStatus(newStatus);
        toast({ title: 'Bank Details Verified Successfully' });
        onComplete(newStatus);
      } else {
        toast({
          title: 'Bank Verification Failed',
          description: result.message || 'Please check your details',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to verify bank details',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Determine if all steps are completed
  const allVerified = status.pan && status.aadhaar && status.bank;
  if(allVerified){
    logTrustScoreEvent(
      userId,
      'kyc_completed',
      50,
     'KYC verification completion found'
    );
  }         
  return (
    <div className="fixed text-gray-700 inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">KYC Verification</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>

        {/* Progress indicator */}
        <div className="flex justify-between mb-6">
          <div className={`text-sm ${status.pan ? 'text-green-500' : 'text-gray-500'}`}>
            PAN {status.pan ? '✓' : '1'}
          </div>
          <div className={`text-sm ${status.aadhaar ? 'text-green-500' : 'text-gray-500'}`}>
            Aadhaar {status.aadhaar ? '✓' : '2'}
          </div>
          <div className={`text-sm ${status.bank ? 'text-green-500' : 'text-gray-500'}`}>
            Bank {status.bank ? '✓' : '3'}
          </div>
        </div>

        {/* PAN Verification */}
        {step === 'pan' && (
          <div className="space-y-4">
            <h4 className="font-medium">PAN Card Verification</h4>
            <Input
              placeholder="PAN Number"
              value={panData.pan}
              onChange={(e) => setPanData({...panData, pan: e.target.value.toUpperCase()})}
              maxLength={10}
            />
            <Input
              placeholder="Full Name as on PAN"
              value={panData.name}
              onChange={(e) => setPanData({...panData, name: e.target.value})}
            />
            <Button onClick={verifyPan} disabled={loading || !panData.pan || !panData.name}>
              {loading ? <Loader /> : status.pan ? 'Already Verified' : 'Verify PAN'}
            </Button>
          </div>
        )}

        {/* Aadhaar OTP Generation */}
        {step === 'aadhaar_otp' && (
          <div className="space-y-4">
            <h4 className="font-medium">Aadhaar Verification</h4>
            <Input
              placeholder="Aadhaar Number"
              value={aadhaarData.aadhaarNumber}
              onChange={(e) => setAadhaarData({
                ...aadhaarData, 
                aadhaarNumber: e.target.value.replace(/\D/g, '').slice(0, 12)
              })}
            />
            <Button 
              onClick={generateAadhaarOTP} 
              disabled={loading || aadhaarData.aadhaarNumber.length !== 12}
            >
              {loading ? <Loader /> : 'Send OTP'}
            </Button>
          </div>
        )}

        {/* Aadhaar OTP Verification */}
        {step === 'aadhaar_verify' && (
          <div className="space-y-4">
            <h4 className="font-medium">Enter Aadhaar OTP</h4>
            <Input
              placeholder="OTP (6 digits)"
              value={aadhaarData.otp}
              onChange={(e) => setAadhaarData({
                ...aadhaarData, 
                otp: e.target.value.replace(/\D/g, '').slice(0, 6)
              })}
            />
            <Button 
              onClick={verifyAadhaarOTP} 
              disabled={loading || aadhaarData.otp.length !== 6}
            >
              {loading ? <Loader /> : status.aadhaar ? 'Already Verified' : 'Verify OTP'}
            </Button>
          </div>
        )}

        {/* Bank Verification */}
        {step === 'bank' && (
          <div className="space-y-4">
            <h4 className="font-medium">Bank Account Verification</h4>
            <Input
              placeholder="Account Number"
              value={bankData.accountNumber}
              onChange={(e) => setBankData({
                ...bankData, 
                accountNumber: e.target.value.replace(/\D/g, '')
              })}
            />
            <Input
              placeholder="IFSC Code"
              value={bankData.ifsc}
              onChange={(e) => setBankData({
                ...bankData, 
                ifsc: e.target.value.toUpperCase()
              })}
            />
            <Input
              placeholder="Account Holder Name"
              value={bankData.name}
              onChange={(e) => setBankData({...bankData, name: e.target.value})}
            />
            <Input
              placeholder="Registered Mobile Number"
              value={bankData.phone}
              onChange={(e) => setBankData({
                ...bankData, 
                phone: e.target.value.replace(/\D/g, '').slice(0, 10)
              })}
            />
            <p className="text-sm text-gray-600">
              By continuing, you are agreeing to <BusinessSLA />
            </p>
            <Button 
              onClick={verifyBankDetails} 
              disabled={
                loading || 
                !bankData.accountNumber || 
                !bankData.ifsc || 
                !bankData.name || 
                !bankData.phone
              }
            >
              {loading ? <Loader /> : status.bank ? 'Already Verified' : 'Verify Bank Details'}
            </Button>
          </div>
        )}

        {/* Skip/Continue buttons */}
        <div className="flex gap-2 mt-4">
          {!allVerified && (
            <Button 
              variant="outline" 
              onClick={() => {
                onComplete(status);
                onClose();
              }}
            >
              Save & Continue Later
            </Button>
          )}
          {step !== 'pan' && (
            <Button 
              variant="outline" 
              onClick={() => {
                if (step === 'bank') setStep('aadhaar_verify');
                else if (step === 'aadhaar_verify') setStep('aadhaar_otp');
                else setStep('pan');
              }}
            >
              Back
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}